import React, { useEffect, useMemo, useRef } from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { assignEventColumns } from '../utils/eventLayout';
import { eventOccursOnDate } from '../utils/eventDate';
import { EventHoverCard } from './EventHoverCard';

interface WeekViewProps {
  currentDate: Date;
  events: CalendarEventResponse[];
  onEventClick: (event: CalendarEventResponse) => void;
  onSlotClick: (date: Date, hour: number, minute: number) => void;
}

const HOUR_HEIGHT = 60; // 60px per hour
const MIN_EVENT_HEIGHT = 30;
const WEEK_DAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onSlotClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate hours (00:00 to 23:00)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Get week days (Sunday to Saturday), memoized
  const weekDays = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentDate]);

  // Pre-compute events and column layouts per day
  const weekData = useMemo(() => {
    return weekDays.map(date => {
      const dayEvents = events.filter(event => eventOccursOnDate(event, date));
      // All-day / birthday events go to the all-day band, not the hourly grid
      const allDayEvents = dayEvents.filter(
        event => event.allDay || event.eventType === 'birthday'
      );
      const timedEvents = dayEvents.filter(
        event => !event.allDay && event.eventType !== 'birthday'
      );
      return {
        date,
        allDayEvents,
        events: timedEvents,
        columnLayout: assignEventColumns(timedEvents),
      };
    });
  }, [weekDays, events]);

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getCurrentTimePosition = (): number | null => {
    const now = new Date();
    const todayDate = weekDays.find(d => isToday(d));

    if (!todayDate) return null;

    const hours = now.getHours();
    const minutes = now.getMinutes();
    return hours * 60 + minutes;
  };

  const formatTime = (hour: number): string => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const getEventPosition = (event: CalendarEventResponse): { top: number; height: number } => {
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);

    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    let endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
    if (endMinutes === startMinutes) endMinutes = Math.min(startMinutes + 30, 24 * 60);
    else if (endMinutes < startMinutes) endMinutes = 24 * 60;

    const top = (startMinutes / 60) * HOUR_HEIGHT;
    const height = ((endMinutes - startMinutes) / 60) * HOUR_HEIGHT;

    return { top, height: Math.max(height, MIN_EVENT_HEIGHT) };
  };

  // Map Google Calendar Color IDs (1-11) to hex colors
  const getEventColor = (colorId?: string, eventType?: string): string => {
    if (eventType === 'birthday') return '#10b981';
    if (eventType === 'activity') return '#f97316';

    const colors: Record<string, string> = {
      '1': '#a4bdfc',
      '2': '#7ae7bf',
      '3': '#dbadff',
      '4': '#ff887c',
      '5': '#fbd75b',
      '6': '#ffb878',
      '7': '#46d6db',
      '8': '#e1e1e1',
      '9': '#5484ed',
      '10': '#51b749',
      '11': '#dc2127',
    };
    return colors[colorId || '9'] || colors['9'];
  };

  const formatEventTime = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    const formatHour = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    return `${formatHour(start)} - ${formatHour(end)}`;
  };

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const currentMinutes = getCurrentTimePosition();
      if (currentMinutes !== null) {
        const scrollPosition = (currentMinutes / 60) * HOUR_HEIGHT - 100;
        scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
      } else {
        scrollContainerRef.current.scrollTop = 8 * HOUR_HEIGHT;
      }
    }
  }, [currentDate]);

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--theme-card-background)' }}>
      {/* Header with day names and dates */}
      <div className="flex border-b sticky top-0 z-10" style={{ borderColor: 'var(--theme-border)', backgroundColor: 'var(--theme-card-background)' }}>
        {/* Time column header */}
        <div className="w-16 flex-shrink-0 border-r" style={{ borderColor: 'var(--theme-border)' }}></div>

        {/* Day headers */}
        {weekDays.map((date, index) => {
          const today = isToday(date);
          return (
            <div
              key={index}
              className="flex-1 py-1.5 flex flex-col items-center border-l"
              style={{ borderColor: 'var(--theme-border)' }}
            >
              <div
                className="text-[11px] font-semibold uppercase"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {WEEK_DAY_SHORT[date.getDay()]}
              </div>
              <div
                className="text-lg font-bold mt-0.5 w-8 h-8 flex items-center justify-center rounded-full tabular-nums"
                style={{
                  color: today ? '#FFFFFF' : 'var(--theme-card-text)',
                  backgroundColor: today ? 'var(--theme-primary)' : 'transparent',
                }}
              >
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day / birthday band */}
      <div className="flex border-b min-h-[26px]" style={{ borderColor: 'var(--theme-border)' }}>
        {/* All-day label */}
        <div
          className="w-16 flex-shrink-0 border-r flex items-center justify-end pr-1.5 text-[9px] font-semibold uppercase"
          style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-muted-text)' }}
        >
          dia inteiro
        </div>
        {/* All-day cells per day */}
        {weekData.map(({ allDayEvents }, dayIndex) => (
          <div
            key={dayIndex}
            className="flex-1 border-l p-[3px] flex flex-col gap-0.5 min-w-0"
            style={{ borderColor: 'var(--theme-border)' }}
          >
            {allDayEvents.map((event) => (
              <EventHoverCard key={event.id} event={event}>
                <div
                  className="h-[18px] rounded-[5px] px-1.5 flex items-center overflow-hidden cursor-pointer text-[10px] font-semibold truncate hover:opacity-90 transition-opacity"
                  style={{
                    backgroundColor: getEventColor(event.colorId, event.eventType),
                    color: '#FFFFFF',
                  }}
                  onClick={() => onEventClick(event)}
                >
                  {event.summary}
                </div>
              </EventHoverCard>
            ))}
          </div>
        ))}
      </div>

      {/* Scrollable timeline */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        <div className="flex">
          {/* Time labels column */}
          <div className="w-16 flex-shrink-0 border-r relative" style={{ borderColor: 'var(--theme-border)' }}>
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-[60px] border-b text-xs text-right pr-2 pt-1"
                style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-muted-text)' }}
              >
                {formatTime(hour)}
              </div>
            ))}
          </div>

          {/* Days columns with time slots */}
          <div className="flex-1 flex relative">
            {weekData.map(({ date, events: dayEvents, columnLayout }, dayIndex) => (
              <div key={dayIndex} className="flex-1 border-r relative" style={{ borderColor: 'var(--theme-border)' }}>
                {/* Time slots */}
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="h-[60px] border-b cursor-pointer hover:bg-opacity-50 transition-colors relative"
                    style={{
                      borderColor: 'var(--theme-border)',
                      backgroundColor: 'transparent',
                    }}
                    onClick={() => onSlotClick(date, hour, 0)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-input-background)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Half-hour line */}
                    <div
                      className="absolute left-0 right-0"
                      style={{
                        top: '50%',
                        height: '1px',
                        backgroundColor: 'var(--theme-border)',
                        opacity: 0.3,
                      }}
                    />
                  </div>
                ))}

                {/* Events overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {dayEvents.map((event) => {
                    const { top, height } = getEventPosition(event);
                    const colInfo = columnLayout.get(event.id);
                    const col = colInfo?.column ?? 0;
                    const totalCols = colInfo?.totalColumns ?? 1;
                    const leftPercent = (col / totalCols) * 100;
                    const widthPercent = (1 / totalCols) * 100;
                    const eventColor = getEventColor(event.colorId, event.eventType);
                    const cancelled = event.status === 'cancelled';

                    return (
                      <EventHoverCard key={event.id} event={event}>
                      <div
                        className="absolute rounded px-1 py-0.5 overflow-hidden cursor-pointer pointer-events-auto hover:opacity-90 transition-opacity"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          left: `calc(${leftPercent}% + 1px)`,
                          width: `calc(${widthPercent}% - 2px)`,
                          backgroundColor: `${eventColor}29`,
                          color: eventColor,
                          borderLeft: `3px solid ${eventColor}`,
                          zIndex: 5,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                      >
                        <div
                          className="text-xs font-semibold truncate"
                          style={cancelled ? { textDecoration: 'line-through' } : undefined}
                        >
                          {event.summary}
                        </div>
                        {height >= 35 && (
                          <div className="text-xs opacity-90 truncate">
                            {formatEventTime(event.startTime, event.endTime)}
                          </div>
                        )}
                        {event.location && height >= 50 && (
                          <div className="text-xs opacity-80 truncate">{event.location}</div>
                        )}
                      </div>
                      </EventHoverCard>
                    );
                  })}
                </div>

                {/* Current time indicator (red line) */}
                {isToday(date) && currentTimePosition !== null && (
                  <div
                    className="absolute left-0 right-0 pointer-events-none"
                    style={{
                      top: `${(currentTimePosition / 60) * HOUR_HEIGHT}px`,
                      zIndex: 10,
                    }}
                  >
                    <div
                      className="absolute left-0 w-2 h-2 rounded-full"
                      style={{ backgroundColor: '#EF4444', top: '-4px', left: '-4px' }}
                    />
                    <div
                      className="h-0.5"
                      style={{ backgroundColor: '#EF4444' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
